import { z } from "zod";
import { COST_LINE_KINDS, type CostLineKind } from "../enums";
import { LIMITS } from "../limits";
import { cents, halfStep, optionalText, uuid } from "./common";

// ---------- Requests ----------

const label = optionalText(LIMITS.costLineLabel, "Label");
const quantity = halfStep(LIMITS.quantityMax);
const unit = optionalText(LIMITS.costLineUnit, "Unit");
const unitCostCents = cents(LIMITS.unitCostCentsMax).nullable();

export const createCostLineBody = z
  .object({
    kind: z.enum(COST_LINE_KINDS),
    /** Required unless expertId is given (then it defaults to "{position} – {name}"). */
    label: label.default(""),
    quantity,
    /** Defaults to "" or "day" for expert_days and per_diem. */
    unit: unit.optional(),
    unitCostCents: unitCostCents.default(null),
    expertId: uuid.nullable().default(null),
    /** AC2 (Marius round 5): the budget line it is paid from. Left out: the activity's line (expert days: the expert's fee line). */
    budgetLineId: uuid.nullable().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.label === "" && v.expertId === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["label"], message: "Label is required." });
    }
  });
export type CreateCostLineBody = z.input<typeof createCostLineBody>;
export type CreateCostLineInput = z.output<typeof createCostLineBody>;

export const updateCostLineBody = z
  .object({
    kind: z.enum(COST_LINE_KINDS).optional(),
    label: label.min(1, "Label is required.").optional(),
    quantity: quantity.optional(),
    unit: unit.optional(),
    unitCostCents: unitCostCents.optional(),
    expertId: uuid.nullable().optional(),
    budgetLineId: uuid.nullable().optional(),
    /** Moves the line to this index (clamped to 0…n-1). */
    position: z.number().int().min(0).optional(),
  })
  .strict();
export type UpdateCostLineBody = z.infer<typeof updateCostLineBody>;

// ---------- Responses ----------

export type CostLine = {
  id: string;
  /** 0-based, dense */
  position: number;
  kind: CostLineKind;
  label: string;
  /** 0..1000, step 0.5 */
  quantity: number;
  unit: string;
  /** null = not entered */
  unitCostCents: number | null;
  /** round(quantity × unitCost), 0 when unitCost is null */
  totalCents: number;
  expertId: string | null;
  /** The budget line it is paid from (AC2); null = none chosen and none found. */
  budgetLineId: string | null;
};

/**
 * AC2 (workbook 27 Oct): the kind of a ToR cost line typed as free text, so
 * it lands on a fitting budget line by default ("Venue and catering" on the
 * venue line, never on a per-diem line called "Other destinations").
 * Expert days are never guessed: they come with an expert.
 */
export function costKindFromLabel(label: string): CostLineKind {
  const t = label.toLowerCase();
  if (/per.?diem|daily allowance|subsistence|\bdsa\b/.test(t)) return "per_diem";
  if (/flight|\btrain\b|travel|ticket|\btaxi\b|transport|airfare|\bbus\b/.test(t)) return "travel";
  if (/venue|\broom|\bhall\b|hire|conference|premises/.test(t)) return "venue";
  if (/cater|coffee|lunch|dinner|meal|refreshment|\bfood\b/.test(t)) return "catering";
  if (/interpret|translat/.test(t)) return "interpretation";
  if (/print|material|publication|stationery|brochure|leaflet|stud(y|ies)/.test(t)) return "materials";
  return "other";
}
